import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../../axios/axiosInstance';
import apiEndpoints from '../../../../../axios/api';
import { localDateString } from '../../../../../utils/helpers/localDate';
import { formatDate } from './tenancyStatus';

/**
 * Mark a tenant inactive (records the last day of the tenancy) or active again
 * (clears the end date). The tenant is never deleted, so the tenancy stays in
 * the flat's history.
 */
const TenantStatusModal = ({ member, onClose, onSuccess }) => {
    const markingInactive = member?.tenancy_status !== 'INACTIVE';
    const [endDate, setEndDate] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (member) setEndDate(localDateString());
    }, [member]);

    if (!member) return null;

    const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
    const unit = [member.flat?.wing?.title, member.flat?.flat_number].filter(Boolean).join(' / ');
    const startDate = member.start_date ? String(member.start_date).slice(0, 10) : '';
    const tooEarly = markingInactive && startDate && endDate && endDate < startDate;

    const handleSubmit = async () => {
        if (markingInactive && !endDate) {
            toast.error('Select the end date');
            return;
        }
        if (tooEarly) {
            toast.error('End date cannot be before the start date');
            return;
        }
        setSaving(true);
        try {
            const body = markingInactive ? { status: 'INACTIVE', end_date: endDate } : { status: 'ACTIVE' };
            const res = await axiosInstance.patch(
                apiEndpoints.SET_TENANT_STATUS_API.replace(':id', member.occupant_id),
                body
            );
            toast.success(res?.message || (markingInactive ? 'Tenant marked inactive' : 'Tenant marked active'));
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to update tenant status');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-md tw-shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon
                            icon={markingInactive ? 'mdi:account-off-outline' : 'mdi:account-check-outline'}
                            className={`tw-text-xl ${markingInactive ? 'tw-text-amber-600' : 'tw-text-emerald-600'}`}
                        />
                        <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">
                            {markingInactive ? 'Mark Tenant Inactive' : 'Mark Tenant Active'}
                        </h3>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                        <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                    </button>
                </div>

                <div className="tw-p-5 tw-space-y-4">
                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-xs tw-space-y-1">
                        <p className="tw-font-medium tw-text-gray-800 tw-text-sm">{fullName}</p>
                        <p className="tw-text-gray-500">{unit || '-'}</p>
                        <p className="tw-text-gray-500">
                            Start date: <span className="tw-text-gray-700">{formatDate(member.start_date)}</span>
                            {member.end_date && (
                                <> &nbsp;·&nbsp; End date: <span className="tw-text-gray-700">{formatDate(member.end_date)}</span></>
                            )}
                        </p>
                    </div>

                    {markingInactive ? (
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                                End date (last day of tenancy) <span className="tw-text-rose-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                min={startDate || undefined}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                            />
                            {tooEarly && (
                                <p className="tw-text-[11px] tw-text-rose-600 tw-mt-1">End date cannot be before the start date.</p>
                            )}
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-2">
                                The tenant keeps portal access up to and including this date and shows as
                                Inactive from the next day. The record stays in the flat&apos;s tenant history.
                            </p>
                        </div>
                    ) : (
                        <p className="tw-text-xs tw-text-gray-600">
                            This clears the end date. The tenant becomes active again and regains portal access.
                        </p>
                    )}
                </div>

                <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                    <button onClick={onClose} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving || tooEarly}
                        className={`tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-rounded-md disabled:tw-opacity-50 ${markingInactive ? 'tw-bg-amber-600 hover:tw-bg-amber-700' : 'tw-bg-emerald-600 hover:tw-bg-emerald-700'}`}
                    >
                        {saving ? 'Saving…' : markingInactive ? 'Mark Inactive' : 'Mark Active'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TenantStatusModal;
